/*****************************************************************
 *  ZOHO BRIDGE PANEL — THE PAGE (/app/bridge)                     *
 *  File: apps/web/src/pages/bridge/BridgePage.js                  *
 *  What it does: the admin panel for the Zoho bridge. A header,   *
 *  a strip that says when Zoho last delivered, and five tabs:     *
 *  Waiting, Prices, Customers, Keys, Log.                         *
 *  Who sees it: owners and admins. The server decides (it answers *
 *  403 to anyone else) and this page says so in plain words.      *
 *  Reads: GET /summary.  Writes: nothing itself (the tabs do).    *
 *****************************************************************/

/*****************************************************************
 *  TABLE OF CONTENTS                                              *
 *  SECTION 1 — THE TABS                                           *
 *  SECTION 2 — THE PAGE                                           *
 *****************************************************************/

import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import InboxTab from './InboxTab';
import PricesTab from './PricesTab';
import CustomersTab from './CustomersTab';
import KeysTab from './KeysTab';
import LogTab from './LogTab';
import { BridgeIcon } from './bridgeIcons';
import { BRIDGE_WORDS } from './bridgeWords';
import { LoadState, formatWhen } from './bridgeParts';
import { fetchSummary } from './bridgeApi';
import './bridge.css';

/*****************************************************************
 *  SECTION 1 — THE TABS                                           *
 *****************************************************************/

// Each tab: its id (also in the address bar), its word, its icon.
const TABS = [
  { id: 'inbox', word: BRIDGE_WORDS.tabInbox, icon: 'inbox' },
  { id: 'prices', word: BRIDGE_WORDS.tabPrices, icon: 'tag' },
  { id: 'customers', word: BRIDGE_WORDS.tabCustomers, icon: 'people' },
  { id: 'keys', word: BRIDGE_WORDS.tabKeys, icon: 'key' },
  { id: 'log', word: BRIDGE_WORDS.tabLog, icon: 'list' },
];

/* ---- countForTab(tabId, summary) -- the little number on a tab ---- */
function countForTab(tabId, summary) {
  if (!summary) {
    return null;
  }

  if (tabId === 'inbox') {
    return summary.pile.proposed.all + summary.pile.failed.all;
  }

  if (tabId === 'prices') {
    return summary.priceCount;
  }

  if (tabId === 'customers') {
    return summary.linkCount;
  }

  if (tabId === 'keys') {
    return summary.activeKeyCount;
  }

  return null;
}

/*****************************************************************
 *  SECTION 2 — THE PAGE                                           *
 *****************************************************************/

export default function BridgePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [summary, setSummary] = useState(null);          // the counts
  const [pageState, setPageState] = useState('loading'); // loading / ready / error / notAllowed

  const askedTab = searchParams.get('tab');
  const isKnownTab = TABS.some((oneTab) => oneTab.id === askedTab);
  const currentTab = isKnownTab ? askedTab : 'inbox';

  /* ---- loadSummary() -- the counts, and whether we may be here ---- */
  const loadSummary = useCallback(async () => {
    const answer = await fetchSummary();

    if (answer.status === 403) {
      setPageState('notAllowed');
      return;
    }

    if (!answer.ok) {
      setPageState('error');
      return;
    }

    setSummary(answer.data);
    setPageState('ready');
  }, []);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  /* ---- chooseTab(tabId) -- switch tab and remember it in the address ---- */
  function chooseTab(tabId) {
    setSearchParams({ tab: tabId });
  }

  return (
    <main className="zb-page">
      <header className="zb-page-head">
        <h1 className="zb-page-title">
          <BridgeIcon name="link" size={28} />
          <span>{BRIDGE_WORDS.pageTitle}</span>
        </h1>
        <p className="zb-page-intro">{BRIDGE_WORDS.pageIntro}</p>
      </header>

      {pageState === 'loading' ? <LoadState state="loading" /> : null}
      {pageState === 'error' ? <LoadState state="error" onRetry={loadSummary} /> : null}

      {pageState === 'notAllowed' ? (
        <div className="zb-notice zb-notice-warn" role="alert">
          <BridgeIcon name="alert" />
          <span>{BRIDGE_WORDS.notAllowed}</span>
        </div>
      ) : null}

      {pageState === 'ready' ? (
        <>
          <p className="zb-heard">
            <BridgeIcon name="clock" />
            <span>
              {BRIDGE_WORDS.lastHeard}:{' '}
              <strong>{summary.lastReceivedAt ? formatWhen(summary.lastReceivedAt) : BRIDGE_WORDS.neverHeard}</strong>
            </span>
          </p>

          <nav className="zb-tabs" role="tablist" aria-label={BRIDGE_WORDS.pageTitle}>
            {TABS.map((oneTab) => {
              const tabCount = countForTab(oneTab.id, summary);
              const isCurrent = oneTab.id === currentTab;

              return (
                <button
                  key={oneTab.id}
                  type="button"
                  role="tab"
                  aria-selected={isCurrent}
                  className={'zb-tab' + (isCurrent ? ' zb-tab-current' : '')}
                  onClick={() => chooseTab(oneTab.id)}
                >
                  <BridgeIcon name={oneTab.icon} />
                  <span>{oneTab.word}</span>
                  {tabCount !== null ? <span className="zb-tab-count">{tabCount}</span> : null}
                </button>
              );
            })}
          </nav>

          <div className="zb-tab-body" role="tabpanel">
            {currentTab === 'inbox' ? <InboxTab onChanged={loadSummary} /> : null}
            {currentTab === 'prices' ? <PricesTab /> : null}
            {currentTab === 'customers' ? <CustomersTab /> : null}
            {currentTab === 'keys' ? <KeysTab onChanged={loadSummary} /> : null}
            {currentTab === 'log' ? <LogTab /> : null}
          </div>
        </>
      ) : null}
    </main>
  );
}
